import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Tpl3plService, InventoryItem } from '../../../services/tpl3pl.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { RawResponseModalComponent } from '../shared/raw-response-modal/raw-response-modal.component';

export type InventorySortColumn = 'stockid' | 'warehouse_code' | 'quantity_on_hand' | 'quantity_available' | 'sync_status' | 'last_sync' | 'internal_qoh';

@Component({
	selector: 'app-tpl3pl-inventory',
	templateUrl: './tpl3pl-inventory.component.html',
	styleUrls: ['./tpl3pl-inventory.component.scss']
})
export class Tpl3plInventoryComponent implements OnInit, OnDestroy {
	inventoryStatus: any = null;
	inventoryItems: InventoryItem[] = [];
	itemListSearch = '';
	inventoryListTotal = 0;
	inventoryListLoading = false;
	inventoryPageIndex = 0;
	inventoryPageSize = 100;
	readonly inventoryPageSizeOptions = [50, 100, 250, 500];
	inventoryStatusFilter: '' | 'synced' | 'pending' | 'error' = '';
	inventorySortColumn: InventorySortColumn = 'last_sync';
	inventorySortDirection: 'asc' | 'desc' = 'desc';
	loading = false;
	syncing = false;
	selectedWarehouse = '';
	lastSyncResult: any = null;
	manualSyncRawLoading = false;
	clearing = false;
	private configSavedSub: Subscription | null = null;
	private searchSub: Subscription | null = null;
	private search$ = new Subject<string>();

	constructor(
		private tpl3plService: Tpl3plService,
		private globalSearchService: GlobalSearchService,
		private dialog: MatDialog
	) {}

	ngOnInit(): void {
		this.loadStatus();
		this.loadInventoryList();
		this.configSavedSub = this.tpl3plService.configSaved$.subscribe(() => this.loadStatus());
		this.searchSub = this.search$.pipe(
			debounceTime(300),
			distinctUntilChanged()
		).subscribe(() => {
			this.inventoryPageIndex = 0;
			this.loadInventoryList();
		});
	}

	ngOnDestroy(): void {
		this.configSavedSub?.unsubscribe();
		this.searchSub?.unsubscribe();
	}

	onInventorySearchInput(value: string): void {
		this.itemListSearch = value ?? '';
		this.search$.next((value || '').trim());
	}

	onRefresh(): void {
		this.inventoryPageIndex = 0;
		this.loadStatus();
		this.loadInventoryList();
	}

	openManualSyncRaw(): void {
		this.manualSyncRawLoading = true;
		this.tpl3plService.getInventorySyncPreview(this.selectedWarehouse || undefined).subscribe({
			next: (res) => {
				this.manualSyncRawLoading = false;
				if (!res.success) {
					this.globalSearchService.showNotification(res.message || 'Could not load request preview', 'warning', 'bottom', 'right');
					return;
				}
				this.dialog.open(RawResponseModalComponent, {
					width: '720px',
					maxWidth: '96vw',
					maxHeight: '90vh',
					data: {
						context: 'inventory',
						warehouse: this.selectedWarehouse || undefined,
						content: { request_payload: res.request_payload },
						onSent: () => {
							this.loadStatus();
							this.inventoryPageIndex = 0;
							this.loadInventoryList();
						}
					},
					panelClass: 'raw-response-dialog'
				});
			},
			error: () => {
				this.manualSyncRawLoading = false;
				this.globalSearchService.showNotification('Could not load request preview', 'danger', 'bottom', 'right');
			}
		});
	}

	loadStatus(): void {
		this.loading = true;
		this.tpl3plService.getInventoryStatus(this.selectedWarehouse || undefined).subscribe({
			next: (result) => {
				this.inventoryStatus = result;
				this.loading = false;
			},
			error: () => {
				this.globalSearchService.showNotification('Error loading inventory status', 'danger', 'bottom', 'right');
				this.loading = false;
			}
		});
	}

	loadInventoryList(): void {
		this.inventoryListLoading = true;
		this.tpl3plService.getInventory({
			warehouse: this.selectedWarehouse || undefined,
			status: this.inventoryStatusFilter || undefined,
			search: (this.itemListSearch || '').trim() || undefined,
			sort: this.inventorySortColumn,
			order: this.inventorySortDirection,
			limit: this.inventoryPageSize,
			offset: this.inventoryPageIndex * this.inventoryPageSize
		}).subscribe({
			next: (res) => {
				const total = res.total ?? 0;
				const maxIndex = total <= 0 ? 0 : Math.max(0, Math.ceil(total / this.inventoryPageSize) - 1);
				if (this.inventoryPageIndex > maxIndex) {
					this.inventoryPageIndex = maxIndex;
					this.loadInventoryList();
					return;
				}
				this.inventoryItems = res.data || [];
				this.inventoryListTotal = total;
				this.inventoryListLoading = false;
			},
			error: () => {
				this.globalSearchService.showNotification('Error loading items at 3PL', 'danger', 'bottom', 'right');
				this.inventoryListLoading = false;
			}
		});
	}

	onInventoryPage(event: { pageIndex: number; pageSize: number }): void {
		this.inventoryPageIndex = event.pageIndex;
		this.inventoryPageSize = event.pageSize;
		this.loadInventoryList();
	}

	clearSyncedInventory(): void {
		if (!confirm('Clear all synced 3PL inventory data and zero the corresponding ERP stock quantities? Use this after the 3PL has cleared their side so you can re-pull from scratch. This cannot be undone.')) {
			return;
		}
		this.clearing = true;
		this.tpl3plService.clearInventorySync().subscribe({
			next: (result) => {
				this.clearing = false;
				if (result.success) {
					this.inventoryStatus = {
						...(this.inventoryStatus || {}),
						total_items: 0,
						synced_items: 0,
						pending_items: 0,
						error_items: 0,
						last_sync_time: null,
						last_sync: null,
						success: true
					};
					this.inventoryItems = [];
					this.inventoryListTotal = 0;
					this.inventoryPageIndex = 0;
					this.globalSearchService.showNotification(result.message || 'Cleared. You can now pull inventory again.', 'success', 'bottom', 'right');
					this.tpl3plService.getInventoryStatus(this.selectedWarehouse || undefined, true).subscribe({
						next: (s) => { this.inventoryStatus = s; },
						error: () => {}
					});
					this.tpl3plService.getInventory({
						warehouse: this.selectedWarehouse || undefined,
						status: this.inventoryStatusFilter || undefined,
						search: (this.itemListSearch || '').trim() || undefined,
						sort: this.inventorySortColumn,
						order: this.inventorySortDirection,
						limit: this.inventoryPageSize,
						offset: 0,
						cacheBust: true
					}).subscribe({
						next: (res) => {
							this.inventoryItems = res.data || [];
							this.inventoryListTotal = res.total ?? 0;
						},
						error: () => {}
					});
				} else {
					this.globalSearchService.showNotification(result.message || 'Clear failed', 'danger', 'bottom', 'right');
				}
			},
			error: () => {
				this.clearing = false;
				this.globalSearchService.showNotification('Error clearing inventory sync data', 'danger', 'bottom', 'right');
			}
		});
	}

	syncInventory(): void {
		this.syncing = true;
		this.lastSyncResult = null;
		this.tpl3plService.syncInventory(this.selectedWarehouse || undefined).subscribe({
			next: (result) => {
				this.lastSyncResult = result;
				if (result.success) {
					this.globalSearchService.showNotification('Synced ' + (result.synced || 0) + ' items', 'success', 'bottom', 'right');
					this.loadStatus();
					this.inventoryPageIndex = 0;
					this.loadInventoryList();
				} else {
					this.globalSearchService.showNotification('Sync failed: ' + result.message, 'danger', 'bottom', 'right');
				}
				this.syncing = false;
			},
			error: (error) => {
				this.lastSyncResult = { success: false, message: error?.error?.message || error?.message || 'Error syncing inventory', response: error?.error?.response ?? error?.error };
				this.globalSearchService.showNotification('Error syncing inventory', 'danger', 'bottom', 'right');
				this.syncing = false;
			}
		});
	}

	setSort(column: InventorySortColumn): void {
		if (this.inventorySortColumn === column) {
			this.inventorySortDirection = this.inventorySortDirection === 'asc' ? 'desc' : 'asc';
		} else {
			this.inventorySortColumn = column;
			this.inventorySortDirection = 'asc';
		}
		this.inventoryPageIndex = 0;
		this.loadInventoryList();
	}

	sortIcon(column: InventorySortColumn): string {
		if (this.inventorySortColumn !== column) { return 'unfold_more'; }
		return this.inventorySortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
	}

	onStatusFilterChange(): void {
		this.inventoryPageIndex = 0;
		this.loadInventoryList();
	}

	get pageDisplayEnd(): number {
		return Math.min(this.inventoryPageIndex * this.inventoryPageSize + this.inventoryPageSize, this.inventoryListTotal);
	}

}
